import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { PublicHeader } from '../components/PublicHeader';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, CheckCircle } from 'lucide-react';
import { Orbs, Reveal } from '../components/landing/primitives';
import { TrustedBy, ToolsGrid, Features, Personas, Stats, Footer } from '../components/landing/sections';
import { ScannerWidget, ScanResult, ScanHistory, ReportDialog } from '../components/landing/scanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getVisitorId = () => {
  let id = localStorage.getItem('vaptara_visitor_id');
  if (!id) { id = `v_${(crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 20)}`; localStorage.setItem('vaptara_visitor_id', id); }
  return id;
};

export default function FreeScanPage() {
  const [visitorId] = useState(getVisitorId);
  const [usage, setUsage] = useState({ used: 0, limit: 5, remaining: 5, exhausted: false });
  const [target, setTarget] = useState('');
  const [scanType, setScanType] = useState('web');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const scannerRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => { fetchUsage(); fetchHistory(); }, []);
  const fetchUsage = async () => { try { const r = await axios.get(`${API}/public/usage`, { params: { visitor_id: visitorId } }); setUsage(r.data); } catch {} };
  const fetchHistory = async () => { try { const r = await axios.get(`${API}/public/scans`, { params: { visitor_id: visitorId } }); setHistory(r.data); } catch {} };

  const runScan = async () => {
    if (!target.trim()) { toast.error('Enter a target URL, IP or app identifier'); return; }
    setScanning(true); setResult(null);
    try {
      const r = await axios.post(`${API}/public/scan`, { visitor_id: visitorId, target: target.trim(), scan_type: scanType });
      setResult(r.data); setUsage(r.data.usage); fetchHistory();
      toast.success(`Scan complete: ${r.data.total_findings} findings`);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    } catch (err) {
      if (err.response?.status === 402) { setUsage(u => ({ ...u, exhausted: true, remaining: 0 })); toast.error(err.response.data.detail); }
      else toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const pickTool = (id) => { setScanType(id); scannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); };
  const openHistory = async (scanId) => { try { const r = await axios.get(`${API}/public/scans/${scanId}`, { params: { visitor_id: visitorId } }); setResult(r.data); setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); } catch {} };

  return (
    <div className="min-h-screen bg-background" data-testid="free-scan-page">
      <PublicHeader usage={usage} />

      <section className="relative overflow-hidden" data-testid="landing-hero">
        <Orbs variant="hero" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}><Badge className="rounded-full bg-primary/10 text-primary border-primary/20 gap-1.5 px-3 py-1 hover:bg-primary/15"><Sparkles className="h-3.5 w-3.5" /> Free online scanners · No sign-in · {usage.limit} scans on us</Badge></motion.div>
            <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold tracking-tight leading-[1.02]" data-testid="free-scan-title">
              Security findings you can <span className="text-gradient">prioritize.</span><br />In web apps, APIs, network & cloud.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }} className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Vaptara discovers, validates and explains security issues in one AI-driven workflow — so you fix proven risk, not theoretical severity. Start with a free scan below.
            </motion.p>
            <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.24 }} className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {['Cut manual triage with confirmed findings', 'Prioritize on proof, not guesswork', 'Report evidence auditors accept'].map(t => <li key={t} className="flex items-center gap-2 text-foreground/80"><CheckCircle className="h-4 w-4 text-primary" />{t}</li>)}
            </motion.ul>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.7 }} className="lg:col-span-5 relative">
            <div className="absolute inset-0 rounded-full pulse-ring border-2 border-primary/30 m-10" />
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-indigo-500/20 bg-[#070B1A] animate-float">
              <img src="/illustrations/hero.jpg" alt="Vaptara shield" className="w-full aspect-square object-cover max-h-[70vh]" />
            </div>
          </motion.div>
        </div>
        <div ref={scannerRef} className="relative max-w-5xl mx-auto px-4 md:px-8 pb-16" id="scanner">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <ScannerWidget target={target} setTarget={setTarget} scanType={scanType} setScanType={setScanType} scanning={scanning} onScan={runScan} usage={usage} />
          </motion.div>
        </div>
      </section>

      <div ref={resultRef} className="max-w-5xl mx-auto px-4 md:px-8 space-y-6 scroll-mt-24">
        {result && <ScanResult result={result} onReport={() => setReportOpen(true)} />}
        <Reveal><ScanHistory history={history} onOpen={openHistory} /></Reveal>
      </div>

      <TrustedBy />
      <ToolsGrid onPick={pickTool} active={scanType} />
      <Features />
      <Stats />
      <Personas />
      <Footer />
      {result && <ReportDialog open={reportOpen} onOpenChange={setReportOpen} scan={result} visitorId={visitorId} />}
    </div>
  );
}
